import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import "../componentStyle/Pre-meeting-join-screen.css"
import { useDeviceManager } from "../hooks/useDeviceManager"
import DeviceDropdown from "./DeviceDropdown"
import ArrowDownIcon from "../new_assets/arrow_down.svg"
import settingsIcon from "../new_assets/settings.svg"
import { useTheme } from "../contexts/ThemeContext"
import PermissionErrorModal from "../modals/PermissionErrorModal"

export default function PreJoinMeetingScreen({ displayName, startCallOnClick, isCallCreator, preJoinSettings, updateStateOfPreJoinSettings, onNameChange, dynamicMessage }) {
    const [name, setName] = useState(displayName || '')
    const [localPreJoinSettings, setLocalPreJoinSettings] = useState(preJoinSettings)
    const videoRef = useRef(null)
    const streamRef = useRef(null)
    const { isDarkTheme } = useTheme()
    
    // Device management
    const {
        // Audio devices
        availableAudioDevices,
        selectedAudioDevice,
        audioError,
        loadAudioDevices,
        selectAudioDevice,
        clearAudioError,
        
        // Video devices
        availableVideoDevices,
        selectedVideoDevice,
        videoError,
        loadVideoDevices,
        selectVideoDevice,
        clearVideoError,
        
        // Combined functions
        loadAllDevices
    } = useDeviceManager({
        autoInitialize: true,
        listenForChanges: true
    });

    // Dropdown states
    const [audioDropdownOpen, setAudioDropdownOpen] = useState(false)
    const [videoDropdownOpen, setVideoDropdownOpen] = useState(false)
    
    // Hover states for dropdown items
    const [hoveredAudioDevice, setHoveredAudioDevice] = useState(null)
    const [hoveredVideoDevice, setHoveredVideoDevice] = useState(null)

    // Permission error modal state
    const [permissionErrorModal, setPermissionErrorModal] = useState({
        isOpen: false,
        deviceType: null // 'camera' or 'microphone'
    })

    // Function to close all dropdowns
    const closeAllDropdowns = () => {
        setAudioDropdownOpen(false);
        setVideoDropdownOpen(false);
    };

    // Function to generate initials from name
    const getInitials = (fullName) => {
        if (!fullName) return "U" // Default if no name

        return fullName
            .split(" ")
            .map(word => word.charAt(0).toUpperCase())
            .join("")
            .substring(0, 2) // Take only first 2 initials
    }

    // Start camera stream with selected device - Memoized to prevent unnecessary recreations
    const startCameraStream = useCallback(async (deviceOverride) => {
        try {
            // Prefer the explicitly provided device, fallback to currently selected
            const targetDevice = deviceOverride || selectedVideoDevice
            const constraints = {
                video: targetDevice ? {
                    deviceId: { exact: targetDevice.deviceId }
                } : true,
                audio: false
            }

            const stream = await navigator.mediaDevices.getUserMedia(constraints)

            if (videoRef.current) {
                videoRef.current.srcObject = stream
                streamRef.current = stream
            }
        } catch (error) {
            console.error("Error accessing camera:", error)
            
            // Check if it's a permission error
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError' || 
                error.message?.includes('permission') || error.message?.includes('Permission')) {
                // Show permission error modal instead of just turning off video
                setPermissionErrorModal({
                    isOpen: true,
                    deviceType: 'camera'
                })
            } else {
                // For other errors, just turn off video
                setLocalPreJoinSettings(prev => ({ ...prev, video: false }))
            }
        }
    }, [selectedVideoDevice])

    // Stop camera stream - Memoized
    const stopCameraStream = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => {
                track.stop()
            })
            streamRef.current = null

            if (videoRef.current) {
                videoRef.current.srcObject = null
            }
        }
    }, [])

    // Handle camera toggle
    const handleCameraToggle = () => {
        const newVideoState = !localPreJoinSettings.video
        setLocalPreJoinSettings({ ...localPreJoinSettings, video: newVideoState })

        if (newVideoState) {
            startCameraStream()
        } else {
            stopCameraStream()
        }
    }

    const handleMute = () => {
        setLocalPreJoinSettings({ ...localPreJoinSettings, audio: !localPreJoinSettings.audio })
    }

    // Handle device selection
    const handleAudioDeviceSelect = (device) => {
        selectAudioDevice(device)
        console.log('Audio device selected:', device.label || device.deviceId)
    }

    const handleVideoDeviceSelect = (device) => {
        selectVideoDevice(device)
        console.log('Video device selected:', device.label || device.deviceId)
        
        // If video is currently on, restart the stream with the newly chosen device immediately
        if (localPreJoinSettings.video) {
            stopCameraStream()
            startCameraStream(device)
        }
    }

    // Handle video state changes AND initial mount
    useEffect(() => {
        if (localPreJoinSettings.video) {
            startCameraStream()
        } else {
            stopCameraStream()
        }

        // Cleanup on unmount
        return () => {
            stopCameraStream()
        }
    }, [localPreJoinSettings.video, startCameraStream, stopCameraStream])

    // Sync name state with displayName prop
    useEffect(() => {
        if (displayName && displayName !== name) {
            setName(displayName)
        }
    }, [displayName, name])

    // Sync local state to parent state including selected devices - Optimized
    useEffect(() => {
        const settingsToUpdate = {
            ...localPreJoinSettings,
            selectedAudioDevice,
            selectedVideoDevice
        };
        
        // Only update if there are actual changes
        updateStateOfPreJoinSettings(settingsToUpdate);
    }, [localPreJoinSettings.video, localPreJoinSettings.audio, selectedAudioDevice?.deviceId, selectedVideoDevice?.deviceId])
    // Note: updateStateOfPreJoinSettings is intentionally omitted to prevent infinite loops if parent doesn't memoize it

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if click is outside of any dropdown elements
            const isClickInsideDropdown = event.target.closest('.feature-pre-join-screen') ||
                                           event.target.closest('.menuList');
            
            if (!isClickInsideDropdown) {
                closeAllDropdowns();
            }
        };

        // Only add listener if any dropdown is open
        if (audioDropdownOpen || videoDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [audioDropdownOpen, videoDropdownOpen]);

    return (
        <div className={`meeting-container ${isDarkTheme ? 'dark' : ''}`}>
            <div className="pre-video-section">
                <div className="video-preview">
                    {!localPreJoinSettings.video ? (
                        <div className="avatar">{getInitials(name)}</div>
                    ) : (
                        <video
                            ref={videoRef}
                            autoPlay
                            muted
                            playsInline
                            className="camera-preview-video"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                transform: 'scaleX(-1)'
                            }}
                        />
                    )}

                    <div className="controls">
                        {/* Microphone Control with Device Selection */}
                        <div className='feature-pre-join-screen' style={{ position: 'relative' }}>
                            <div onClick={handleMute} style={{ display: 'flex', alignItems: 'center' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                {!localPreJoinSettings.audio ? (
                                    <>
                                        <line x1="1" y1="1" x2="23" y2="23"></line>
                                        <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
                                        <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path>
                                        <line x1="12" y1="19" x2="12" y2="23"></line>
                                        <line x1="8" y1="23" x2="16" y2="23"></line>
                                    </>
                                ) : (
                                    <>
                                        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                                        <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                                        <line x1="12" y1="19" x2="12" y2="23"></line>
                                        <line x1="8" y1="23" x2="16" y2="23"></line>
                                    </>
                                )}
                            </svg>
                                <span className='sep'></span>
                            </div>
                            <img 
                                src={ArrowDownIcon} 
                                className='arrow' 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    // If already open, just close it
                                    if (audioDropdownOpen) {
                                        setAudioDropdownOpen(false);
                                    } else {
                                        // Close all other dropdowns first
                                        closeAllDropdowns();
                                        // Open audio dropdown
                                        setAudioDropdownOpen(true);
                                        loadAudioDevices();
                                    }
                                }}
                                style={{ cursor: 'pointer', marginLeft: '5px' }}
                            />
                            <div 
                                className={`menuList ${audioDropdownOpen ? 'active' : ''}`}
                                onClick={(e) => e.stopPropagation()}
                            >
                                    <span className='title'>Microphone</span>
                                    <div className='options'>
                                        {/* <div className='item' style={{ fontSize: '10px', color: '#666', backgroundColor: '#f0f0f0' }}>
                                            {availableAudioDevices.length} devices found
                                        </div> */}
                                        {audioError ? (
                                            <div className='item' style={{ color: !isDarkTheme ? '#000' : '#fff', fontSize: '12px' }}>
                                                {audioError}
                                                {/* {audioError.includes('denied') && (
                                                    <div 
                                                        className='item' 
                                                        style={{ 
                                                            color: '#FF5722', 
                                                            fontSize: '12px', 
                                                            cursor: 'pointer',
                                                            marginTop: '5px',
                                                            textDecoration: 'underline'
                                                        }}
                                                        onClick={() => {
                                                            clearAudioError();
                                                            loadAudioDevices();
                                                        }}
                                                    >
                                                        Click to retry
                                                    </div>
                                                )} */}
                                            </div>
                                        ) : availableAudioDevices.length === 0 ? (
                                            <div className='item' style={{ color: '#666', fontSize: '12px' }}>
                                                No microphone detected
                                            </div>
                                        ) : (
                                            availableAudioDevices.map((device, index) => (
                                                <div 
                                                    key={device.deviceId || index}
                                                    className={`item ${selectedAudioDevice?.deviceId === device.deviceId ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        handleAudioDeviceSelect(device);
                                                        setAudioDropdownOpen(false);
                                                    }}
                                                    onMouseEnter={() => setHoveredAudioDevice(device.deviceId)}
                                                    onMouseLeave={() => setHoveredAudioDevice(null)}
                                                    style={{
                                                        cursor: 'pointer',
                                                        backgroundColor: selectedAudioDevice?.deviceId === device.deviceId 
                                                            ? 'rgba(0, 0, 0, 0.15)' 
                                                            : hoveredAudioDevice === device.deviceId 
                                                                ? 'rgba(0, 0, 0, 0.1)' 
                                                                : 'transparent',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        borderLeft: selectedAudioDevice?.deviceId === device.deviceId || hoveredAudioDevice === device.deviceId
                                                            ? '3px solid #F25920' 
                                                            : '3px solid transparent',
                                                        paddingLeft: selectedAudioDevice?.deviceId === device.deviceId || hoveredAudioDevice === device.deviceId
                                                            ? '9px' 
                                                            : '12px',
                                                        fontWeight: selectedAudioDevice?.deviceId === device.deviceId ? '600' : '500',
                                                        color: selectedAudioDevice?.deviceId === device.deviceId ? '#ffffff' : 'inherit',
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                >
                                                    <span style={{ fontSize: '12px' }}>
                                                        {device.label || `Microphone ${index + 1}`}
                                                    </span>
                                                    {selectedAudioDevice?.deviceId === device.deviceId && (
                                                        <span style={{ color: '#FF5722', fontSize: '14px' }}>✓</span>
                                                    )}
                                                </div>
                                            ))
                                        )}
                                    </div>
                            </div>
                        </div>

                        {/* Camera Control with Device Selection */}
                        <div className='feature-pre-join-screen' style={{ position: 'relative' }}>
                            <div onClick={handleCameraToggle} style={{ display: 'flex', alignItems: 'center' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                {!localPreJoinSettings.video ? (
                                    <>
                                        <line x1="1" y1="1" x2="23" y2="23"></line>
                                        <path d="M21 21H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3m3-3h6l2 3h4a2 2 0 0 1 2 2v9.34m-7.72-2.06a4 4 0 1 1-5.56-5.56"></path>
                                    </>
                                ) : (
                                    <>
                                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                                        <circle cx="12" cy="13" r="4"></circle>
                                    </>
                                )}
                            </svg>
                                <span className='sep'></span>
                            </div>
                            <img 
                                src={ArrowDownIcon} 
                                className='arrow' 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    // If already open, just close it
                                    if (videoDropdownOpen) {
                                        setVideoDropdownOpen(false);
                                    } else {
                                        // Close all other dropdowns first
                                        closeAllDropdowns();
                                        // Open video dropdown
                                        setVideoDropdownOpen(true);
                                        loadVideoDevices();
                                    }
                                }}
                                style={{ cursor: 'pointer', marginLeft: '5px' }}
                            />
                            <div 
                                className={`menuList ${videoDropdownOpen ? 'active' : ''}`}
                                onClick={(e) => e.stopPropagation()}
                            >
                                    <span className='title'>Camera</span>
                                    <div className='options'>
                                        {/* <div className='item' style={{ fontSize: '10px', color: '#666' }}>
                                            {availableVideoDevices.length} cameras found
                                        </div> */}
                                        {videoError ? (
                                            <div className='item' style={{ color: !isDarkTheme ? '#000' : '#fff', fontSize: '12px' }}>
                                                {videoError}
                                                {/* {videoError.includes('denied') && (
                                                    <div 
                                                        className='item' 
                                                        style={{ 
                                                            color: '#FF5722', 
                                                            fontSize: '12px', 
                                                            cursor: 'pointer',
                                                            marginTop: '5px',
                                                            textDecoration: 'underline'
                                                        }}
                                                        onClick={() => {
                                                            clearVideoError();
                                                            loadVideoDevices();
                                                        }}
                                                    >
                                                        Click to retry
                                                    </div>
                                                )} */}
                                            </div>
                                        ) : availableVideoDevices.length === 0 ? (
                                            <div className='item' style={{ color: '#666', fontSize: '12px' }}>
                                                No camera detected
                                            </div>
                                        ) : (
                                            availableVideoDevices.map((device, index) => (
                                                <div 
                                                    key={device.deviceId || index}
                                                    className={`item ${selectedVideoDevice?.deviceId === device.deviceId ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        handleVideoDeviceSelect(device);
                                                        setVideoDropdownOpen(false);
                                                    }}
                                                    onMouseEnter={() => setHoveredVideoDevice(device.deviceId)}
                                                    onMouseLeave={() => setHoveredVideoDevice(null)}
                                                    style={{
                                                        cursor: 'pointer',
                                                        backgroundColor: selectedVideoDevice?.deviceId === device.deviceId 
                                                            ? 'rgba(0, 0, 0, 0.15)' 
                                                            : hoveredVideoDevice === device.deviceId 
                                                                ? 'rgba(0, 0, 0, 0.1)' 
                                                                : 'transparent',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        borderLeft: selectedVideoDevice?.deviceId === device.deviceId || hoveredVideoDevice === device.deviceId
                                                            ? '3px solid #F25920' 
                                                            : '3px solid transparent',
                                                        paddingLeft: selectedVideoDevice?.deviceId === device.deviceId || hoveredVideoDevice === device.deviceId
                                                            ? '9px' 
                                                            : '12px',
                                                        fontWeight: selectedVideoDevice?.deviceId === device.deviceId ? '600' : '500',
                                                        color: selectedVideoDevice?.deviceId === device.deviceId ? '#ffffff' : 'inherit',
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                >
                                                    <span style={{ fontSize: '12px' }}>
                                                        {device.label || `Camera ${index + 1}`}
                                                    </span>
                                                    {selectedVideoDevice?.deviceId === device.deviceId && (
                                                        <span style={{ color: '#FF5722', fontSize: '14px' }}>✓</span>
                                                    )}
                                                </div>
                                            ))
                                        )}
                                    </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="join-section">
                <h1 className="title">Ready to join the meeting</h1>
                <p className="subtitle">Setup your audio and video before joining</p>
                <div className={`dynamic-message-container ${dynamicMessage ? 'has-message' : ''}`}>
                    {dynamicMessage && <p className="dynamic-message">{dynamicMessage}</p>}
                </div>
                <div className="input-container">
                    <input
                        type="text"
                        value={name}
                        readOnly
                        onChange={(e) => {
                            const newName = e.target.value;
                            setName(newName);
                            if (onNameChange) {
                                onNameChange(newName);
                            }
                        }}
                        className="name-input"
                        placeholder="Enter your name"
                    />
                </div>

                <button className="join-button" onClick={startCallOnClick}>
                    {isCallCreator ? "Start Meeting" : "Join Meeting"}
                </button>
            </div>

            {/* Permission Error Modal */}
            <PermissionErrorModal
                isOpen={permissionErrorModal.isOpen}
                deviceType={permissionErrorModal.deviceType}
                onClose={() => {
                    setPermissionErrorModal({ isOpen: false, deviceType: null })
                    if (permissionErrorModal.deviceType === 'camera') {
                        setLocalPreJoinSettings(prev => ({ ...prev, video: false }))
                    } else if (permissionErrorModal.deviceType === 'microphone') {
                        setLocalPreJoinSettings(prev => ({ ...prev, audio: false }))
                    }
                }}
            />
        </div>
    )
}